import {
  ScrollArea,
  SearchInput,
  cn,
  SheetContent,
  Sheet,
} from "web-design-system";
import { XIcon } from "lucide-react";
import { useState, memo } from "react";
import { useMediaQuery } from "usehooks-ts";
import { EditColumnsDialog } from "@core/components/table/edit-fields-dialog";
import { VisibilityState } from "@tanstack/react-table";
import { useFundsTableState } from "@funds/hooks/use-funds-table-state";
import { useFundsTableConfig } from "@funds/hooks/use-funds-table-config";
import { FilterRenderer } from "./filter-renderer";

export type FilterUpdaterFn = (field: string, value: string[]) => void;

export const FundsFilter = memo(
  ({
    showFilters,
    setShowFilters,
    onFilterVisibilityChange,
  }: {
    showFilters: boolean;
    setShowFilters: (show: boolean) => void;
    onFilterVisibilityChange: (fc: VisibilityState) => void;
  }) => {
    const { filters, updateFilters } = useFundsTableState();

    const [filterSearch, setFilterSearch] = useState("");

    const { filterConfig, setFilterConfig, onFiltersOrderChange } =
      useFundsTableConfig();

    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const content = (
      <>
        <ScrollArea className="size-full border relative pb-16 bg-card border-border rounded-lg">
          <div className="flex flex-col relative flex-1 gap-4 px-4 py-3">
            <div className="flex justify-between items-center">
              <h6>Filters</h6>
            </div>
            <div className="relative border border-input focus-within:ring-ring focus-within:ring-2 rounded-lg">
              <SearchInput
                placeholder="Search"
                className="border-none w-full !ring-0 pr-8"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
              <XIcon
                onClick={() => setFilterSearch("")}
                className={cn(
                  "absolute right-2 hidden size-4 top-1/2 -translate-y-1/2",
                  filterSearch && "block"
                )}
              />
            </div>
            <FilterRenderer
              filters={filters}
              filterSearch={filterSearch}
              onFiltersChange={updateFilters}
              filterConfig={filterConfig}
            />
          </div>
        </ScrollArea>
        <div className="p-4 absolute bottom-0 left-0 right-0">
          <EditColumnsDialog
            columnsConfig={filterConfig}
            header={"Customise filters"}
            label="Edit filters"
            onReorder={onFiltersOrderChange}
            onVisibilityChange={(changes) => {
              setFilterConfig((fc) => {
                const changeKeys = Object.keys(changes);
                return fc.map((c) =>
                  changeKeys.includes(c.id as string)
                    ? {
                        id: c.id,
                        visible: changes[c.id as string],
                      }
                    : c
                );
              });
              onFilterVisibilityChange(changes);
            }}
          />
        </div>
      </>
    );

    if (isDesktop) {
      return (
        <div className="flex h-full relative w-sidebar flex-col rounded-lg shrink-0">
          {content}
        </div>
      );
    }

    return (
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent className="p-0 w-80">{content}</SheetContent>
      </Sheet>
    );
  }
);

FundsFilter.displayName = "FundsFilter";
